// src/components/Login.js
import React, { useState } from 'react';
import axios from 'axios';
import './Login.css';

// cria um client apontando para a MESMA ORIGEM (Nginx proxy /auth -> 7007)
const api = axios.create({
  baseURL: '/auth',
  withCredentials: true, // envia/recebe o cookie 'sid'
});

function normalizePerms(raw) {
  if (Array.isArray(raw)) {
    return raw.map((p) => String(p || '').trim().toLowerCase()).filter(Boolean);
  }
  if (raw == null) return [];
  const s = String(raw).trim();
  if (!s) return [];
  if (s.startsWith('{') && s.endsWith('}')) {
    return s
      .slice(1, -1)
      .split(',')
      .map((p) => p.trim().replace(/^"+|"+$/g, ''))
      .filter(Boolean)
      .map((p) => p.toLowerCase());
  }
  return s.split(/[,\s]+/).map((p) => p.trim().toLowerCase()).filter(Boolean);
}

function isSafeLocalPath(path) {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//');
}

function scorePathname(path) {
  const raw = String(path || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw, 'https://sonikcenter.sonik.cloud').pathname.replace(/\/+$/, '') || '/';
  } catch (_) {
    return raw.split('?')[0].replace(/\/+$/, '') || '/';
  }
}

/** Painel de vendedoras / legado — financeiroadm não deve cair aqui no pós-login. */
function isSellerScorePath(path) {
  const p = scorePathname(path);
  const bare = p.replace(/^\/score(?=\/|$)/, '') || '/';
  return [
    '/finance-vendedoras',
    '/finance-sellers',
    '/finance',
    '/vendedoras-consulta',
    '/vendedoras-fluxo',
    '/vendedoras',
  ].includes(bare);
}

/**
 * Destino pós-login: ?redirect= (nginx) ou ?next= (painel-score).
 * financeiroadm → Consulta SPC (/score), mesmo se o redirect apontar ao painel de vendedoras.
 * Vendedora pura vai ao dashboard (card do painel). Só segue um /score/*
 * se o redirect já apontar para lá.
 */
function resolvePostLoginPath(permissoes) {
  const perms = normalizePerms(permissoes);
  const has = (p) => perms.includes(p);
  const soApenasVendedora =
    has('vendedor') && !has('admin') && !has('financeiro') && !has('financeiroadm');

  let raw = '';
  try {
    const params = new URLSearchParams(window.location.search || '');
    raw = params.get('redirect') || params.get('next') || '';
  } catch (_) {}

  // Painel score admin: aprovação manual, SPC detalhado. Não o fluxo de vendedoras.
  if (has('financeiroadm') && !has('admin')) {
    if (isSafeLocalPath(raw) && !isSellerScorePath(raw) && raw !== '/dashboard' && raw !== '/') {
      return raw;
    }
    return '/score';
  }

  if (soApenasVendedora) {
    if (isSafeLocalPath(raw) && raw.startsWith('/score/')) {
      if (raw === '/score/finance' || raw === '/score/finance-sellers') {
        return '/score/finance-vendedoras';
      }
      return raw;
    }
    return '/dashboard';
  }

  // Financeiro (quase sempre também tem vendedor): nunca mandar ao /score/finance legado
  if (has('financeiro') || has('admin')) {
    if (raw === '/score/finance' || raw === '/finance') {
      return '/score/finance-vendedoras';
    }
  }

  if (isSafeLocalPath(raw)) return raw;
  return '/dashboard';
}

function IconEye({ off = false }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      {off ? (
        <>
          <path d="M3 3l18 18" />
          <path d="M10.6 10.6a2 2 0 102.8 2.8" />
          <path d="M9.9 5.1A9.8 9.8 0 0112 5c5 0 9.3 3.1 11 7.5a11.7 11.7 0 01-4.2 5.1" />
          <path d="M6.1 6.1A11.7 11.7 0 001 12.5C2.7 16.9 7 20 12 20a9.9 9.9 0 005.9-1.9" />
        </>
      ) : (
        <>
          <path d="M1 12.5C2.7 8.1 7 5 12 5s9.3 3.1 11 7.5C21.3 16.9 17 20 12 20S2.7 16.9 1 12.5z" />
          <circle cx="12" cy="12.5" r="3" />
        </>
      )}
    </svg>
  );
}

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      const loginRes = await api.post('/login', { username, password });
      const { token } = loginRes.data || {};

      const { data } = await api.get('/me');
      const { email, userNome, userLogin, permissoes } = data;

      localStorage.setItem('userEmail', email);
      localStorage.setItem('userNome', userNome);
      localStorage.setItem('userLogin', userLogin);
      localStorage.setItem('userPermissoes', JSON.stringify(permissoes || []));
      if (token) localStorage.setItem('ssoToken', token);

      window.location.href = resolvePostLoginPath(permissoes);
    } catch (err) {
      console.error('Erro ao fazer login:', err);
      setErro(err.response?.data?.message || 'Usuário ou senha inválidos. Tente novamente.');
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-brand">
          <img src="/assets/sonikcenterfont.png" alt="Sonik Center" />
          <div>
            <p className="login-kicker">Portal interno</p>
            <h1>Tudo da operação em um só lugar.</h1>
            <p className="login-lead">
              Suporte, financeiro, compras, solicitações e os demais painéis da Sonik, com o seu cargo.
            </p>
            <div className="login-meta">
              <span>Acesso restrito</span>
              <span>Ambiente interno</span>
            </div>
          </div>
        </aside>

        <section className="login-panel">
          <h2>Entrar</h2>
          <p>Use o mesmo usuário e senha do MK.</p>

          <form className="login-form" autoComplete="off" onSubmit={handleLogin}>
            <div className="login-field">
              <label htmlFor="login-usuario">Usuário</label>
              <div className="login-input-wrap">
                <input
                  id="login-usuario"
                  type="text"
                  name="username"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="seu.usuario"
                />
              </div>
            </div>

            <div className="login-field">
              <label htmlFor="login-senha">Senha</label>
              <div className="login-input-wrap">
                <input
                  id="login-senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="login-toggle"
                  onClick={() => setMostrarSenha((v) => !v)}
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <IconEye off={mostrarSenha} />
                </button>
              </div>
            </div>

            {erro && <p className="login-error">{erro}</p>}

            <button type="submit" className="login-submit" disabled={enviando}>
              {enviando ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <p className="login-foot">Sonik Center • uso interno</p>
        </section>
      </div>
    </div>
  );
};

export default Login;
